'use client'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { desktopVw } from '../../styles/utils'
import { getCurrentUser, isLoggedIn, validateEmail } from '../../utils'
import useCustomer from '../../hooks/useCustomer'
import useReferralPoints from '../../hooks/useReferralPoints'
import AuthWrapper from '../../components/Auth/AuthWrapper'
import FormFooter from '../../components/Auth/FormFooter'
import { Container, ErrorMessage, Input } from '../../components/FormElements'
import Success from '../../components/ReferAndEarn/Success'
import { BUILD_ENVIRONMENTS } from '@src/config/constants'
import { API_SF_RUNTIME_BASE_URL, CONFIG_REPORTING_SERVICES } from '@src/config/env'
import appData from '@src/quince-site-common/src/helpers/appData'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent, trackEvent } from '@src/telemetry/track'
import RichTextComp from '../../../../quince-components/src/molecules/RichText'
import RichText from '../../../../quince-site-common/src/domains/RichText'
import { ReferralFormHeader, StyledButton } from '../../components/ReferAndEarn/ReferralStyle'
import { captureEvent } from '../../components/Sentry/utils'
import useBearerFetch from '../../hooks/useBearerFetch'
import useDetectDevices from '../../hooks/useDetectDevices'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'

const StyledReferAndEarn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledInput = styled(Input)`
  padding: 16px 16px 0px;
  height: 50px;
  width: 314px;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding: ${desktopVw(16)} ${desktopVw(16)} 0;
    width: ${desktopVw(350)};
  }
`

const StyledRefFooter = styled(FormFooter)`
  margin: 0 30px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;

  p {
    display: block;
    text-align: center;
    white-space: pre-wrap;
    font-size: 13px;
    line-height: 19px;
    font-family: ${props => props.theme.fonts.grosa};

    a {
      font-size: 13px;
      line-height: 19px;
      margin: unset;
      font-weight: unset;
      text-transform: unset;
      letter-spacing: unset;
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin: 0 0 ${desktopVw(92)};

    p {
      font-size: ${desktopVw(13)};
      line-height: ${desktopVw(19)};
      max-width: ${desktopVw(600)};

      a {
        font-size: ${desktopVw(13)};
        line-height: ${desktopVw(19)};
      }
    }
  }
`

const ReferAndEarn = ({ data }) => {
  const router = useRouter()
  const page = data.authenticationPage
  const [stage, setStage] = useState(0)
  const [formData, setFormData] = useState({})
  const [errorMsg, setErrorMsg] = useState('')

  const { fetchData: fetchCustomerDetails } = useBearerFetch()

  const { email } = getCurrentUser()

  const { customer } = useCustomer()

  useEffect(() => {
    if (email) {
      setFormData({
        sourceEmail: email,
        referralLink: '',
      })
    }
  }, [email])

  useEffect(() => {
    if (formData.sourceEmail && customer) {
      fetchCustomerDetails(`${API_SF_RUNTIME_BASE_URL}/customer-details`)
    }
  }, [formData.sourceEmail, customer, fetchCustomerDetails])

  const handleChange = e => {
    setErrorMsg('')

    const { name } = e.target
    const { value } = e.target
    setFormData({
      ...formData,
      [name]: value,
      referralLink: '',
    })
  }

  const isReferralApiSupported = appData.environmentType !== BUILD_ENVIRONMENTS.STAGE
  const { fetchData, response, error, loading } = useBearerFetch()
  const url = `${CONFIG_REPORTING_SERVICES}/v2-referralEmails/sendEmailToReferred`

  const handleSubmit = e => {
    e.preventDefault()

    const { sourceEmail, targetEmail } = formData

    if (!validateEmail(sourceEmail)) {
      setErrorMsg('Please enter a valid email address')
      return
    }

    if (!targetEmail) {
      setErrorMsg('Please enter at least one Email')
      return
    }

    if (!isLoggedIn() && !getIsNativeViewClient()) {
      const url = new URL(window.location.href)
      url.searchParams.set('targetEmail', targetEmail)
      router.push(`/log-in?redirecturi=${encodeURIComponent(url.href)}`)
    }

    const targetEmailArr = targetEmail.split(',')

    for (const e of targetEmailArr) {
      if (!validateEmail(e.trim())) {
        setErrorMsg(`${e} is not a valid email address`)
        return
      }
    }

    if (!loading) {
      const body = { ...formData, targetEmail: targetEmailArr }

      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }

      isReferralApiSupported && fetchData(url, options)
      trackEvent(TELEMETRY_EVENT_NAMES.SEND_REFERRAL_EMAIL_INVITE, {
        email: sourceEmail,
        targetEmail,
      })
      trackClickEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
        trackGA: true,
        trackInternal: true,
        category: 'referral',
        action: 'send_invite',
      })
    }
  }

  const [referralCode, setReferralCode] = useState('')

  const { fetchReferrals, successfulReferrals, invitedReferrals } = useReferralPoints(formData.sourceEmail)
  const redirectUri = isBrowser() ? new URL(window.location.href) : undefined
  const targetEmail = redirectUri?.searchParams.get('targetEmail')

  useEffect(() => {
    if (targetEmail) {
      setFormData(data => ({
        ...data,
        targetEmail,
      }))
    }
  }, [targetEmail])

  useEffect(() => {
    if (!response && !error) {
      return
    }

    if (response?.code) {
      setReferralCode(response.code)
      // Reset field
      setFormData(formData => ({
        ...formData,
        targetEmail: '',
      }))
      setStage(1)
      fetchReferrals()
    } else if (response?.error) {
      setErrorMsg('An error occurred while referring your friends.')
      captureEvent(
        `Error in sending referral email. Server responded with error: ${response.error} and message: ${response.message}. Sent by : ${formData.sourceEmail} & Sent to: ${formData.targetEmail}.`,
      )
    } else if (error) {
      setErrorMsg('An error occurred while referring your friends.')
      captureEvent(
        `Error in sending referral email. There was an error : ${error} in sending referrals. Sent by : ${formData.sourceEmail} & Sent to: ${formData.targetEmail}`,
      )
    } else if (response && !response.code) {
      captureEvent(
        `Error in sending referral email. Empty response code. Sent by : ${formData.sourceEmail} & Sent to: ${formData.targetEmail}`,
      )
    }
    // TODO: Need to properly fix the dependencies array as current implementation causes infinite loop if all dependencies are added
  }, [response, error])

  const { devices } = useDetectDevices()
  const richTextsData = page?.richText?.map(item => RichText.makeRichTextProps(RichText.fromContentful(item)))
  return (
    <>
      <StyledReferAndEarn>
        <AuthWrapper
          image={devices.mobile ? page.imageMobile : page.image}
          referImage
          imageText={<RichTextComp {...richTextsData[0]} />}
          stage={stage}
          referLayout
        >
          {stage === 0 && (
            <form onSubmit={handleSubmit}>
              <ReferralFormHeader marginTop={isLoggedIn()}>
                <RichTextComp {...richTextsData[1]} />
              </ReferralFormHeader>
              <Container>
                <StyledInput
                  type="email"
                  placeholder="Your email"
                  name="sourceEmail"
                  onChange={handleChange}
                  defaultValue={email}
                  readOnly={isLoggedIn()}
                />
              </Container>
              <Container>
                <StyledInput
                  type="text"
                  name="targetEmail"
                  placeholder="Your friend’s email (separated by commas)"
                  onChange={handleChange}
                  value={formData.targetEmail}
                />
              </Container>
              <Container>
                <StyledButton black type="submit" loading={loading}>
                  {page.buttonText}
                </StyledButton>
              </Container>
              {errorMsg && (
                <Container>
                  <ErrorMessage alignCenter>{errorMsg}</ErrorMessage>
                </Container>
              )}
            </form>
          )}
          {stage === 1 && (
            <Success
              handleSubmit={handleSubmit}
              targetEmail={formData.targetEmail}
              referralCode={referralCode}
              handleChange={handleChange}
              successfulReferrals={successfulReferrals}
              invitedReferrals={invitedReferrals}
              submitButtonText={page.buttonText}
              loading={loading}
            />
          )}
        </AuthWrapper>
        {stage === 0 && (
          <StyledRefFooter>
            <RichTextComp {...richTextsData[2]} />
          </StyledRefFooter>
        )}
      </StyledReferAndEarn>
    </>
  )
}

export default ReferAndEarn

// export const ReferAndEarnQuery = graphql`
//   query ReferAndEarn($id: String!) {
//     contentfulAuthenticationPage(id: {eq: $id}) {
//       id
//       slug
//       seoTitle
//       seoDescripton
//       image {
//         description
//         gatsbyImageData(width: 3840)
//       }
//       imageMobile {
//         description
//         gatsbyImageData(width: 3840)
//       }
//       imageText {
//         imageText
//       }
//       formHeader {
//         json: raw
//       }
//       formFooter {
//         json: raw
//       }
//       buttonText
//       richText {
//         id
//         commonClassNames
//         mobileClassNames
//         dynamicValueClassesMobile
//         baseDynamicValueClasses
//         startFrom
//         endOn
//         content {
//           raw
//           references {
//             baseDynamicValueClasses
//             content {
//               raw
//             }
//             contentful_id
//           }
//         }
//       }
//     }
//   }
// `;
